// src/componentes/Comisiones/ComisionistasList.jsx
// Pantalla de administración: lista de comisionistas.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaSearch, FaUserTie } from "react-icons/fa";
import { listenComisionistas } from "../../services/comisionistasService";
import { useUserContext } from "../GestionDeUsuarios/UserContext";
import "./Comisiones.css";

const formatPct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`;

const ESQUEMA_LABELS = {
  "enganche-100": "100% al enganche",
  "enganche-50-mensualidades-50": "50/50 mensualidades",
  "escalonado-por-pagos": "Escalonado",
  custom: "Personalizado",
};

export default function ComisionistasList() {
  const navigate = useNavigate();
  const { can } = useUserContext();
  const [items, setItems] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const unsub = listenComisionistas({ soloActivos: false }, (data) => {
      setItems(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return items
      .filter((c) => {
        if (!mostrarInactivos && c.activo === false) return false;
        if (!q) return true;
        const texto = `${c.nombre || ""} ${c.apellidos || ""} ${c.email || ""} ${c.telefono || ""}`.toLowerCase();
        return texto.includes(q);
      })
      .sort((a, b) =>
        `${a.nombre} ${a.apellidos}`.localeCompare(`${b.nombre} ${b.apellidos}`)
      );
  }, [items, busqueda, mostrarInactivos]);

  if (loading) {
    return (
      <div className="com-page">
        <div className="com-panel">
          <p style={{ color: "#94a3b8" }}>Cargando comisionistas…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="com-page">
      <div className="com-panel">
        {/* ─── Encabezado ─────────────────────────────── */}
        <div className="com-head">
          <div>
            <h2>Comisionistas</h2>
            <p>Catálogo de vendedores externos. Cada comisionista tiene un proveedor espejo para el flujo de CxP.</p>
          </div>
          {can("comisionistas.gestionar") && (
            <div className="com-head-actions">
              <button
                className="btn btn-primary"
                onClick={() => navigate("/app/comisionistas/nuevo")}
              >
                <FaPlus style={{ marginRight: 6 }} />
                Nuevo comisionista
              </button>
            </div>
          )}
        </div>

        {/* ─── Filtros ─────────────────────────────────── */}
        <div className="com-filters">
          <div style={{ position: "relative", flex: 1 }}>
            <FaSearch
              style={{
                position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                color: "#9ca3af", fontSize: 13,
              }}
            />
            <input
              className="com-search"
              style={{ paddingLeft: 30 }}
              placeholder="Buscar por nombre, email o teléfono…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
          <label className="com-toggle-inactive">
            <input
              type="checkbox"
              checked={mostrarInactivos}
              onChange={(e) => setMostrarInactivos(e.target.checked)}
            />
            Mostrar inactivos
          </label>
        </div>

        {/* ─── Grid ────────────────────────────────────── */}
        {filtrados.length === 0 ? (
          <div className="com-empty">
            <FaUserTie style={{ fontSize: 28, marginBottom: 8, color: "#d1d5db" }} />
            <div>
              {busqueda
                ? "No se encontraron comisionistas con ese criterio."
                : "Aún no hay comisionistas registrados."}
            </div>
          </div>
        ) : (
          <ul className="com-grid">
            {filtrados.map((c) => (
              <li key={c.id} className="com-card">
                <div className="com-card-head">
                  <div>
                    <div className="com-card-name">
                      {c.nombre} {c.apellidos}
                      {c.activo === false && (
                        <span className="com-badge-inactivo" style={{ marginLeft: 8 }}>
                          Inactivo
                        </span>
                      )}
                    </div>
                    <span className="com-card-tipo">
                      {c.tipo === "interno" ? "Interno" : "Externo"}
                    </span>
                  </div>
                  <span className="com-card-tarifa">{formatPct(c.tarifaPorDefecto)}</span>
                </div>

                <div className="com-card-rows">
                  {c.email && (
                    <div className="com-card-row">
                      <span>Email</span>
                      <b style={{ fontSize: 12 }}>{c.email}</b>
                    </div>
                  )}
                  {c.telefono && (
                    <div className="com-card-row">
                      <span>Teléfono</span>
                      <b>{c.telefono}</b>
                    </div>
                  )}
                  <div className="com-card-row">
                    <span>Esquema</span>
                    <b style={{ fontSize: 12 }}>
                      {ESQUEMA_LABELS[c.esquemaPorDefecto] || c.esquemaPorDefecto}
                    </b>
                  </div>
                  {c.banco && (
                    <div className="com-card-row">
                      <span>Banco</span>
                      <b>{c.banco}</b>
                    </div>
                  )}
                </div>

                <div className="com-card-actions">
                  {can("comisionistas.gestionar") && (
                    <button
                      className="btn-link"
                      onClick={() => navigate(`/app/comisionistas/${c.id}/editar`)}
                    >
                      Editar
                    </button>
                  )}
                  <button
                    className="btn-link"
                    onClick={() => navigate(`/app/comisionistas/${c.id}`)}
                  >
                    Ver detalle →
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
